/*
Species detail screen — fiche-espèce, refonte v1.6.0 « Herbier ».
Mirrors Android SpeciesDetailScreen.kt : hero mosaïque photos de référence
(S9/S10, ou hero texte primaryContainer), galerie des captures user,
carte d'identité (SpeciesIdGrid), bloc « Reconnaître » (descriptions
terrain + frises floraison/fructification 12 mois), « Forces &
faiblesses », « Son rôle en ville » (repliable), « À Paris » fusionné
(prose + stats + PDF), « À propos » (summary Wikipedia espèce, repli
genre, ou message vide), remarquables de l'espèce, « Voir sur la carte ».

Topbar minimaliste (retour seul) — le nom et le rang « #N / M » vivent
dans le hero. Les fiches `(G, sp.)` (unknownSpecies) redirigent vers la
fiche genre ; les espèces non capturées retombent sur la map (verrou).
There is deliberately NO inline mini-map: Android has none, and a second
MapLibre instance leaked a WebGL context that got the tab evicted during
the next camera capture.
*/

import { navigate, kPaths, type Route } from '../router'
import {
	ensureSpeciesIndex,
	speciesNameFor,
	displayName,
	formatLatin,
	type SpeciesName,
} from '../data/speciesIndex'
import {
	ensureSpeciesInfo,
	speciesInfoFor,
	validBitfield,
	isMonthInBitfield,
	type SpeciesEntry,
	type SpeciesEss,
	type SpeciesIdDesc,
	type SpeciesSvc,
} from '../data/speciesInfo'
import { ensureRemarquablesInfo, type RemarquableEntry } from '../data/remarquablesInfo'
import { ensureGenreInfo, genreInfoFor, type GenreEntry } from '../data/genreInfo'
import { ensureSpeciesPhotos, photosFor, licenseFor, photoUrl, type SpeciesPhotos } from '../data/speciesPhotos'
import { listCaptures, deleteCapture } from '../data/captureRepository'
import { loadPhoto } from '../data/photoStore'
import { ensureArbresFeatures, type ArbreFeature } from '../data/arbresFeatures'
import { ensureArbreAttrs, attrsFor } from '../data/arbreAttrs'
import { ensureDatasetStats } from '../data/datasetStats'
import { arrKeyLabel } from '../data/arrKey'
import { confirmDeleteCapture } from './components/DeleteCaptureDialog'
import { openLightbox, type LightboxPhoto, type LightboxOptions } from './components/PhotoLightbox'
import { renderSkeletonBlock, renderSkeletonLines } from './components/Skeleton'
import type { Capture } from '../data/types'

const kFrenchDate = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
const kFrenchNumber = new Intl.NumberFormat('fr-FR')
// Parité AParisBlock.kt FR_PERCENT / FR_RATIO : 1 décimale fixe (min = max),
// Locale.FRENCH — « 7,3 % » (espace insécable avant %) et « ×4,7 ».
// roundingMode halfEven : java.text.NumberFormat (AParisBlock.kt) arrondit
// en HALF_EVEN par défaut, Intl en half-expand — sans lui, ~40 valeurs du
// dataset divergent d'un cran (« ×2,3 » PWA vs « ×2,2 » Android). Le cast
// vient de la lib TS ES2022 (roundingMode est typé en ES2023) ; les moteurs
// antérieurs à Intl v3 ignorent l'option (retour half-expand, toléré).
const kPercentFr1 = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1, roundingMode: 'halfEven' } as Intl.NumberFormatOptions)
const kRatioFr1 = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1, roundingMode: 'halfEven' } as Intl.NumberFormatOptions)

interface RemarquableForSpeciesRow
{
	arbreId: number
	entry: RemarquableEntry | null  // null when remarquables-info.json doesn't carry this id (stale dataset)
	captured: boolean  // mirrors the user's captures: drives the locked-row vs clickable-row branch
}

interface ViewModel
{
	speciesName: SpeciesName | null
	speciesInfo: SpeciesEntry | null
	captures: Capture[]
	remarquables: RemarquableForSpeciesRow[]  // every remarquable whose ArbreFeature.speciesIndex matches inSk, sorted captured-first then by qualif
	catalogueTotal: number  // totalEspecesIdentifiees — dénominateur du rang topbar « #N / 784 »
	genreFallback: GenreEntry | null  // repli « À propos du genre » quand l'espèce n'a pas de summary (parité SpeciesDetailScreen.kt)
	refPhotos: SpeciesPhotos | null  // photos de référence S9/S10 — null = espèce non illustrée, hero texte
	refLicenseName: string | null  // nom de licence de la principale (obligation CC-BY sur la ligne d'attribution)
	loadError: string | null
}

let fActiveObjectUrls: string[] = []  // photo blob URLs created by the current mount, revoked on remount

/*
Mount the species detail screen for the route's `sk`. Renders a static
shell synchronously, hydrates async, then lazily loads photos.
*/
export function mountSpeciesScreen(inRoot: HTMLElement, inRoute: Route): void
{
	revokeObjectUrls()  // clean up any URLs left by the previous mount

	if (inRoute.id !== 'species')
	{
		inRoot.innerHTML = '<div class="screen"><div class="placeholder">Route invalide.</div></div>'
		return
	}

	const vSk = inRoute.sk
	const vCelebrate = inRoute.celebrate === true
	const vHighlight = typeof inRoute.highlight === 'number' ? inRoute.highlight : null

	inRoot.innerHTML = renderShell(vCelebrate)
	inRoot.querySelector('#nav-back')?.addEventListener('click', () => history.length > 1 ? history.back() : navigate(kPaths.arboretum()))
	void hydrate(inRoot, vSk, vHighlight)
}

function renderShell(inCelebrate: boolean): string
{
	const vBanner = inCelebrate
		? '<div class="celebration-banner">✨ Nouvelle espèce capturée !</div>'
		: ''
	// Skeleton mirrors the populated layout's stack — hero card (mosaïque
	// 220px + lignes identité), stats card, mini-map card, wiki card. Same
	// vertical rhythm so the page doesn't reflow when content arrives.
	const vSkeleton = `
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '100%', height: '220px', radius: '12px' })}
			<div class="species-skeleton-identity-text">
				${renderSkeletonBlock({ width: '70%', height: '20px' })}
				${renderSkeletonBlock({ width: '45%', height: '13px' })}
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '38%', height: '15px' })}
			<div class="species-skeleton-stack">
				${renderSkeletonLines(2, '12px')}
			</div>
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '100%', height: '48px', radius: '24px' })}
		</section>
		<section class="species-card species-card-skeleton">
			${renderSkeletonBlock({ width: '32%', height: '15px' })}
			<div class="species-skeleton-stack">
				${renderSkeletonLines(3, '12px')}
			</div>
		</section>
	`
	// Topbar minimaliste — parité SpeciesDetailTopBar : plus de titre ni de
	// rang (déplacés dans le hero). On ne garde que le bouton retour.
	return `
		<div class="screen species">
			<header class="arboretum-header">
				<button class="nav-back" id="nav-back" aria-label="Retour">←</button>
			</header>
			${vBanner}
			<div class="species-body" id="species-body">
				${vSkeleton}
			</div>
		</div>
	`
}

async function hydrate(inRoot: HTMLElement, inSk: number, inHighlightId: number | null): Promise<void>
{
	const vBody = inRoot.querySelector<HTMLElement>('#species-body')
	if (!vBody) return

	const vModel = await loadViewModel(inSk)  // gather everything before painting once

	if (vModel.loadError)
	{
		vBody.innerHTML = `<div class="placeholder species-error">⚠ ${escapeHtml(vModel.loadError)}</div>`
		return
	}

	// Deep link historique vers une fiche `(G, sp.)` — parité
	// SpeciesDetailScreen.kt l.90-96 : les placeholders unknownSpecies
	// n'ont pas de fiche espèce, on redirige vers la fiche du genre.
	// AVANT le verrou capture, comme le redirect Android précède toute
	// autre logique d'écran (décision produit actée).
	if (vModel.speciesName?.unknownSpecies)
	{
		navigate(kPaths.genre(vModel.speciesName.genre))
		return
	}

	// Verrou parité Android v1.1.0+ : la fiche-espèce n'est accessible
	// qu'aux espèces déjà capturées par l'utilisateur. Un deep-link
	// (URL bar, link partagé) vers /#/species/{sk non capturée} retombe
	// silencieusement sur la map — l'écran cible pop la page côté Android.
	if (vModel.captures.length === 0)
	{
		navigate(kPaths.map())
		return
	}

	vBody.innerHTML = renderViewModel(inSk, vModel)
	// Wire the clickable remarquable rows — locked rows are <div>s with
	// no data-arbre-id and aria-disabled, so this selector skips them.
	vBody.querySelectorAll<HTMLElement>('.species-rmq-row[data-arbre-id]').forEach((inRow) => {
		inRow.addEventListener('click', () => {
			const vId = Number(inRow.dataset.arbreId)
			if (Number.isFinite(vId)) navigate(kPaths.remarquable(vId))
		})
	})
	// The display name handed to the delete dialog mirrors the title:
	// cascade nv → nc → latin (parity Android displayNomCommun).
	const vDisplayName = vModel.speciesName ? displayName(vModel.speciesName) : 'Espèce inconnue'
	void hydrateGallery(inRoot, inSk, vModel.captures, inHighlightId, vDisplayName)
	// "Voir sur la carte" → persistent map filtered to this species (Android
	// ShowOnMapButton → map_filtered/{sk}).
	vBody.querySelector('#species-show-on-map')?.addEventListener('click', () => {
		navigate(kPaths.mapFiltered(inSk))
	})
	// Toggle « Son rôle en ville » — recomposition sèche Android (`expanded`)
	// traduite en bascule `hidden`, sans animation.
	const vRoleHeader = vBody.querySelector<HTMLButtonElement>('#species-role-header')
	vRoleHeader?.addEventListener('click', () => {
		const vItems = vBody.querySelector<HTMLElement>('#species-role-items')
		const vChevron = vBody.querySelector<HTMLElement>('#species-role-chevron')
		if (!vItems) return
		const vExpanded = vItems.hidden  // état APRÈS bascule
		vItems.hidden = !vExpanded
		vRoleHeader.setAttribute('aria-expanded', String(vExpanded))
		if (vChevron) vChevron.textContent = vExpanded ? '▴' : '▾'
	})
	// Troncature « À propos » — parité SummaryText(collapsedLines = 5) :
	// le toggle ne s'affiche que si le clamp CSS déborde réellement
	// (équivalent hasVisualOverflow), mesuré après layout via rAF. Guards
	// null-safe : navigation rapide = éléments détruits avant le rAF.
	const vWikiText = vBody.querySelector<HTMLElement>('#species-wiki-text')
	const vWikiToggle = vBody.querySelector<HTMLButtonElement>('#species-wiki-toggle')
	if (vWikiText && vWikiToggle)
	{
		requestAnimationFrame(() => {
			if (!vWikiText.isConnected) return
			if (vWikiText.scrollHeight > vWikiText.clientHeight) vWikiToggle.hidden = false
		})
		vWikiToggle.addEventListener('click', () => {
			const vClamped = vWikiText.classList.toggle('species-wiki-text-clamped')
			vWikiToggle.textContent = vClamped ? 'Lire la suite' : 'Réduire'
		})
	}
	// Tuiles de la mosaïque hero → lightbox de référence, SANS options
	// (parité ReferencePhotoLightbox : ni delete ni jump-to-map). Caption =
	// attribution CC-BY « auteur · licence » + lien source ↗ par photo.
	const vRefPhotos = vModel.refPhotos
	if (vRefPhotos)
	{
		vBody.querySelectorAll<HTMLElement>('.species-hero-tile[data-ref-index]').forEach((inTile) => {
			inTile.addEventListener('click', () => {
				const vIdx = Number(inTile.dataset.refIndex)
				if (!Number.isFinite(vIdx)) return
				const vPhotos: LightboxPhoto[] = vRefPhotos.all.map((inPhoto) => ({
					url: photoUrl(inPhoto),
					caption: `${inPhoto.by}${vModel.refLicenseName ? ` · ${vModel.refLicenseName}` : ''}`,
					sourceUrl: inPhoto.u ?? undefined,
				}))
				openLightbox(vPhotos, vIdx)
			})
		})
	}
}

async function loadViewModel(inSk: number): Promise<ViewModel>
{
	try
	{
		const [, , vRmqInfo, vFeatures, vAllCaptures, , vStats] = await Promise.all([
			ensureSpeciesIndex(),
			ensureSpeciesInfo(),
			ensureRemarquablesInfo(),
			ensureArbresFeatures(),
			listCaptures(),
			ensureArbreAttrs(),  // arr des remarquables verrouillés (« ??? · 13e »)
			ensureDatasetStats(),
			ensureGenreInfo(),  // repli « À propos du genre » (WikipediaBlock fallback)
			ensureSpeciesPhotos(),  // hero mosaïque — ne throw jamais (asset absent → tables vides)
		])
		const vCaptures = vAllCaptures.filter((inC) => inC.speciesIndex === inSk)  // user's photos for this species

		// Cross-ref every remarquable with the user's captures: the arbreId
		// gates the cell's clickability (parity Android LockedRemarquableRow).
		const vCapturedArbres = new Set<number>(vAllCaptures.map((inC) => inC.arbreId))
		const vRows: RemarquableForSpeciesRow[] = []
		for (const vF of vFeatures)
		{
			if (vF.speciesIndex !== inSk || !vF.remarquable) continue
			vRows.push({
				arbreId: vF.id,
				entry: vRmqInfo.get(vF.id) ?? null,
				captured: vCapturedArbres.has(vF.id),
			})
		}
		// Sort: captured first, then by qualification (so paysager/historique
		// stick together inside each block), then by arbreId for determinism.
		vRows.sort((inA, inB) => {
			if (inA.captured !== inB.captured) return inA.captured ? -1 : 1
			const vQa = inA.entry?.qualif ?? 'zzz'
			const vQb = inB.entry?.qualif ?? 'zzz'
			if (vQa !== vQb) return vQa.localeCompare(vQb, 'fr')
			return inA.arbreId - inB.arbreId
		})

		const vSpeciesName = speciesNameFor(inSk)
		const vSpeciesInfo = speciesInfoFor(inSk)
		// Espèces sans page Wikipedia dédiée (~32 % des identifiées, surtout
		// hybrides/cultivars/sous-espèces) : repli sur le résumé du GENRE,
		// étiqueté explicitement pour ne pas faire passer le texte du genre
		// pour celui de l'espèce (parité SpeciesDetailScreen.kt genreFallback).
		let vGenreFallback: GenreEntry | null = null
		if (isBlank(vSpeciesInfo?.summary) && vSpeciesName)
		{
			const vGenreEntry = genreInfoFor(vSpeciesName.genre)
			if (vGenreEntry && !isBlank(vGenreEntry.summary)) vGenreFallback = vGenreEntry
		}

		// Photos de référence (S9/S10) : hero mosaïque + attribution CC-BY.
		// `null` → hero texte (parité SpeciesDetailScreen.kt refPhotos).
		const vRefPhotos = photosFor(inSk)
		const vRefLicenseName = vRefPhotos ? (licenseFor(vRefPhotos.principal.lic)?.name ?? null) : null

		return {
			speciesName: vSpeciesName,
			speciesInfo: vSpeciesInfo,
			captures: vCaptures,
			remarquables: vRows,
			catalogueTotal: vStats.totalEspecesIdentifiees ?? 0,
			genreFallback: vGenreFallback,
			refPhotos: vRefPhotos,
			refLicenseName: vRefLicenseName,
			loadError: null,
		}
	}
	catch (vErr)
	{
		const vMsg = vErr instanceof Error ? vErr.message : 'Erreur inconnue'
		return { speciesName: null, speciesInfo: null, captures: [], remarquables: [], catalogueTotal: 0, genreFallback: null, refPhotos: null, refLicenseName: null, loadError: vMsg }
	}
}

function renderViewModel(inSk: number, inModel: ViewModel): string
{
	const vInfo = inModel.speciesInfo

	// Hero — parité SpeciesHero.kt : mosaïque photos de référence +
	// identité (eyebrow rang + nom + binôme) + attribution, ou hero texte.
	const vIdentity = renderHero(inModel)

	// « À Paris » fusionné — parité AParisBlock.kt : absorbe l'ancien
	// StatsBlock ET le bloc PDF standalone. Monté dès que `info` existe
	// (le compteur est alors garanti), même sans `ess`.
	const vAParis = vInfo ? renderAParis(vInfo) : ''
	const vRmqSection = renderRemarquablesSection(inModel.remarquables)
	// « À propos » — inconditionnel (l'item Android est toujours monté) :
	// summary espèce, sinon repli genre, sinon message vide.
	const vWiki = renderWikiOrFallback(inModel)

	const vGallery = renderGalleryShell(inSk, inModel.captures.length)
	// Carte d'identité — parité SpeciesIdGrid.kt : ne rend que si `ess`
	// présent, la garde interne 0-paire fait le reste.
	const vIdGrid = vInfo?.ess ? renderIdGrid(vInfo.ess) : ''
	// Mois courant 0-based (Date.getMonth()) calculé une seule fois — parité
	// ReconnaitreBlock.kt `currentMonth = LocalDate.now().monthValue` (1-based,
	// décalage absorbé par isMonthInBitfield côté TS).
	const vMonth0 = new Date().getMonth()
	const vReconnaitre = vInfo?.ess ? renderReconnaitre(vInfo.ess, vMonth0) : ''
	const vForces = vInfo?.ess ? renderForcesFaiblesses(vInfo.ess) : ''
	// « Son rôle en ville » — le call-site Android ne monte le bloc que si
	// EcosystemServices non-null ; le JSON omet les groupes svc vides.
	const vRole = vInfo?.ess?.svc ? renderRoleEnVille(vInfo.ess.svc) : ''
	const vMap = `
		<section class="species-card">
			<button class="species-action" id="species-show-on-map">Voir sur la carte</button>
		</section>
	`

	// Ordre des sections — parité SpeciesDetailScreen.kt (LazyColumn
	// l.158-256) : Hero → Galerie captures → Carte d'identité →
	// Reconnaître → Forces & faiblesses → Son rôle en ville → À Paris →
	// À propos → Remarquables → Voir sur la carte.
	return `
		${vIdentity}
		${vGallery}
		${vIdGrid}
		${vReconnaitre}
		${vForces}
		${vRole}
		${vAParis}
		${vWiki}
		${vRmqSection}
		${vMap}
	`
}

/*
Hero de la fiche espèce — parité SpeciesHero.kt. Deux modes selon la
couverture photo (S9/S10) :
- `refPhotos` null (espèce non illustrée) → IdentityBlock texte historique
  (card primaryContainer, eyebrow rang + nom + binôme).
- sinon → une seule card : mosaïque de référence (220px) façon collage
  Guide des essences, puis le même bloc identité, puis la ligne
  d'attribution discrète (obligation CC-BY). L'identité vit SOUS la photo
  (pas de scrim, lisibilité Fraunces).
Le rang « #N / M » migre de la topbar vers l'eyebrow (mêmes conditions :
pokedexNumber non-null, pas unknownSpecies, catalogueTotal > 0).
*/
function renderHero(inModel: ViewModel): string
{
	const vName = inModel.speciesName
	const vTitle = vName ? displayName(vName) : 'Espèce inconnue'
	const vLatin = vName ? formatLatin(vName) : ''
	const vSubtitle = vTitle !== vLatin && vLatin.length > 0 ? vLatin : ''
	const vRank = vName && vName.pokedexNumber !== null && !vName.unknownSpecies && inModel.catalogueTotal > 0
		? `#${vName.pokedexNumber} / ${inModel.catalogueTotal}`
		: null
	// Contenu identité mutualisé entre les deux modes (parité IdentityContent).
	const vIdentityContent = `
		${vRank ? `<div class="species-hero-eyebrow">${escapeHtml(vRank)}</div>` : ''}
		<h1 class="display species-title">${escapeHtml(vTitle)}</h1>
		${vSubtitle ? `<div class="species-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
	`

	const vPhotos = inModel.refPhotos
	if (!vPhotos)
	{
		// Hero texte — le padding/gap de la card (species-card-identity) suffit.
		return `
			<section class="species-card species-card-identity">
				${vIdentityContent}
			</section>
		`
	}

	// Mode photo : la card passe en padding 0 (.species-hero) pour que la
	// mosaïque soit clippée par le border-radius ; l'identité et
	// l'attribution reprennent leur padding en interne.
	return `
		<section class="species-card species-card-identity species-hero">
			${renderHeroMosaic(vPhotos, vTitle)}
			<div class="species-hero-identity">
				${vIdentityContent}
			</div>
			${renderHeroAttribution(vPhotos, inModel.refLicenseName)}
		</section>
	`
}

/*
Mosaïque de référence (gouttières 2px, hauteur 220px) — parité PhotoMosaic :
1 photo → principale pleine largeur ; ≥ 2 → principale flex 2 à gauche +
colonne flex 1 de 1-3 détails. Pas de coins arrondis par tuile — la card
parente clippe. data-ref-index alignés sur `all` (0 = principale) pour la
lightbox (wiring à venir, étape C3). aria-label : nom de l'espèce sur la
principale (parité contentDescription), « Photo de détail n » sur les
détails (un bouton doit rester nommé, contrairement au null Compose).
*/
function renderHeroMosaic(inPhotos: SpeciesPhotos, inSpeciesName: string): string
{
	const vPrincipal = `
		<button class="species-hero-tile species-hero-tile-principal" data-ref-index="0" aria-label="${escapeAttr(inSpeciesName)}">
			<img class="species-hero-img" src="${escapeAttr(photoUrl(inPhotos.principal))}" alt="" loading="lazy">
		</button>
	`
	if (inPhotos.details.length === 0)
	{
		return `<div class="species-hero-mosaic">${vPrincipal}</div>`
	}
	const vDetails = inPhotos.details.map((inPhoto, inI) => `
		<button class="species-hero-tile" data-ref-index="${inI + 1}" aria-label="Photo de détail ${inI + 1}">
			<img class="species-hero-img" src="${escapeAttr(photoUrl(inPhoto))}" alt="" loading="lazy">
		</button>
	`).join('')
	return `
		<div class="species-hero-mosaic">
			${vPrincipal}
			<div class="species-hero-details">${vDetails}</div>
		</div>
	`
}

/*
Ligne d'attribution discrète sous l'identité (obligation CC-BY : crédit
visible) — parité HeroAttributionLine : « Photo : {auteur} · {licence} »
(« Photos : » si plusieurs), lien vers la page source quand une URL existe
(l'attribution est portée par la principale, partagée par construction).
*/
function renderHeroAttribution(inPhotos: SpeciesPhotos, inLicenseName: string | null): string
{
	const vPrefix = inPhotos.all.length > 1 ? 'Photos' : 'Photo'
	const vLabel = `${vPrefix} : ${inPhotos.principal.by}${inLicenseName ? ` · ${inLicenseName}` : ''}`
	const vUrl = inPhotos.principal.u
	if (vUrl)
	{
		return `
			<a class="species-hero-attribution" href="${escapeAttr(vUrl)}" target="_blank" rel="noopener">
				<span class="species-hero-attribution-label">${escapeHtml(vLabel)}</span>
				<span aria-hidden="true">↗</span>
			</a>
		`
	}
	return `<div class="species-hero-attribution"><span class="species-hero-attribution-label">${escapeHtml(vLabel)}</span></div>`
}

/*
Bloc « Carte d'identité » — parité stricte SpeciesIdGrid.kt. Grille 2
colonnes de paires label/valeur remplaçant les anciennes pills. Ordre
fixe, chaque paire omise si sa valeur est absente/vide ; carte non rendue
si aucune paire disponible.
*/
function buildIdPairs(inEss: SpeciesEss): Array<[string, string]>
{
	const vPairs: Array<[string, string]> = []
	if (inEss.port) vPairs.push(['Silhouette', inEss.port])
	if (inEss.feuillage) vPairs.push(['Feuillage', inEss.feuillage])
	if (inEss.haut) vPairs.push(['Hauteur', inEss.haut])
	if (inEss.env) vPairs.push(['Envergure', inEss.env])
	// Repli catégorie floue (« Grand ») uniquement en l'absence de hauteur
	// ET d'envergure — parité SpeciesIdGrid.kt.
	if (!inEss.haut && !inEss.env && inEss.taille) vPairs.push(['Taille', inEss.taille])
	if (inEss.croiss) vPairs.push(['Croissance', capitalizeFirstFr(inEss.croiss)])
	if (inEss.long) vPairs.push(['Longévité', longeviteValue(inEss.long)])
	// Origine = origine + indigénat joints par « · », omise si les deux absents.
	const vOrigine = [inEss.origine, inEss.indigenat].filter((inV) => !!inV && inV.length > 0).join(' · ')
	if (vOrigine.length > 0) vPairs.push(['Origine', vOrigine])
	if (inEss.fam) vPairs.push(['Famille', inEss.fam])
	if (inEss.expo && inEss.expo.length > 0)
	{
		vPairs.push(['Exposition', inEss.expo.map(capitalizeFirstFr).join(' · ')])
	}
	// `fleurs` volontairement absent (redondant avec la future frise floraison).
	return vPairs
}

/*
1re lettre en majuscule locale fr, reste inchangé — parité `capitalizeFirst`
(Kotlin Locale.FRENCH) : ne titlecase que si le 1er caractère est minuscule.
*/
function capitalizeFirstFr(inText: string): string
{
	if (inText.length === 0) return inText
	const vFirst = inText.charAt(0)
	const vUpper = vFirst.toLocaleUpperCase('fr-FR')
	return vFirst === vUpper ? inText : vUpper + inText.slice(1)
}

/*
Longévité affichable — parité `longeviteValue` (SpeciesIdGrid.kt) : si la
valeur brute contient une parenthèse, ne garder que son contenu
(« Moyenne (100 à 200 ans) » → « 100 à 200 ans ») ; sinon valeur brute.
*/
function longeviteValue(inRaw: string): string
{
	const vOpen = inRaw.indexOf('(')
	const vClose = inRaw.indexOf(')', vOpen + 1)
	return vOpen >= 0 && vClose > vOpen ? inRaw.substring(vOpen + 1, vClose).trim() : inRaw
}

function renderIdGrid(inEss: SpeciesEss): string
{
	const vPairs = buildIdPairs(inEss)
	if (vPairs.length === 0) return ''  // garde de tête — parité `if (pairs.isEmpty()) return`
	const vCells = vPairs.map(([inLabel, inValue]) => `
		<div class="species-idgrid-cell">
			<div class="species-idgrid-label">${escapeHtml(inLabel)}</div>
			<div class="species-idgrid-value">${escapeHtml(inValue)}</div>
		</div>
	`).join('')
	return `
		<section class="species-card">
			<h3 class="species-section-title">Carte d'identité</h3>
			<div class="species-idgrid">${vCells}</div>
		</section>
	`
}

/* Initiales des 12 mois (janvier → décembre), alignées sur les bitfields. */
const kMonthLetters: string[] = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

/*
Bloc « Reconnaître » — parité ReconnaitreBlock.kt : repères visuels terrain
(écorce, feuillage) + frises floraison / fructification 12 mois. Purement
statique — aucune animation (contrat device de Max). Garde de tête : rien
si idDesc absent et les deux bitfields invalides (côté Kotlin les bitfields
arrivent déjà validés null ; ici validBitfield() joue ce rôle).
*/
function renderReconnaitre(inEss: SpeciesEss, inMonth0: number): string
{
	const vIdDesc: SpeciesIdDesc | null = inEss.iddesc ?? null
	const vFlor = validBitfield(inEss.flor)
	const vFruct = validBitfield(inEss.fruct)
	if (!vIdDesc && vFlor === null && vFruct === null) return ''

	const vParts: string[] = []
	if (vIdDesc?.ecorce) vParts.push(renderLabelledLine('Écorce', stripLabelPrefix(vIdDesc.ecorce, 'Écorce')))
	if (vIdDesc?.feuillage) vParts.push(renderLabelledLine('Feuillage', stripLabelPrefix(vIdDesc.feuillage, 'Feuillage')))

	// En-tête des 12 lettres de mois (mois courant en évidence), dès qu'une
	// frise (floraison ou fructification) sera rendue.
	if (vFlor !== null || vFruct !== null)
	{
		const vLetters = kMonthLetters.map((inLetter, inI) => {
			const vCurrent = inI === inMonth0
			return `<span class="species-month-letter${vCurrent ? ' species-month-letter-current' : ''}">${inLetter}</span>`
		}).join('')
		vParts.push(`<div class="species-month-letters" aria-hidden="true">${vLetters}</div>`)
	}

	if (vFlor !== null || vIdDesc?.floraison)
	{
		vParts.push(renderSeasonGroup('Floraison', vIdDesc?.floraison ?? null, vFlor, 'species-month-cell-flor', inMonth0))
	}
	if (vFruct !== null || vIdDesc?.fructification)
	{
		vParts.push(renderSeasonGroup('Fructification', vIdDesc?.fructification ?? null, vFruct, 'species-month-cell-fruct', inMonth0))
	}

	// « En floraison ce mois-ci » — pastille or + bodyMedium onSurfaceVariant.
	if (vFlor !== null && isMonthInBitfield(vFlor, inMonth0))
	{
		vParts.push(`
			<div class="species-flor-now">
				<span class="species-flor-now-dot" aria-hidden="true"></span>
				<span>En floraison ce mois-ci</span>
			</div>
		`)
	}

	return `
		<section class="species-card species-card-reconnaitre">
			<h3 class="species-section-title">Reconnaître</h3>
			${vParts.join('')}
		</section>
	`
}

/*
Groupe « label annoté (+ description) » suivi de sa frise 12 mois si le
bitfield existe — parité SeasonGroup (ReconnaitreBlock.kt). La description
passe par stripLabelPrefix seulement si présente, comme le `?.let` Kotlin.
*/
function renderSeasonGroup(
	inLabel: string,
	inDesc: string | null,
	inBits: number | null,
	inActiveClass: string,
	inMonth0: number,
): string
{
	const vDesc = inDesc ? stripLabelPrefix(inDesc, inLabel) : null
	const vStrip = inBits !== null ? renderMonthStrip(inBits, inActiveClass, inMonth0) : ''
	return `
		<div class="species-season-group">
			${renderLabelledLine(inLabel, vDesc)}
			${vStrip}
		</div>
	`
}

/*
Ligne « label — description » — parité LabelledLine (ReconnaitreBlock.kt) :
label semibold puis texte optionnel. Le séparateur Kotlin est littéralement
"  —  " (deux espaces de chaque côté) ; HTML collapse les espaces
consécutifs, donc nbsp + espace de chaque côté — même largeur visuelle, en
gardant des points de césure.
*/
function renderLabelledLine(inLabel: string, inText: string | null): string
{
	const vTail = inText && inText.trim().length > 0
		? `&nbsp; —&nbsp; ${escapeHtml(inText)}`
		: ''
	return `<div class="species-labelled-line"><span class="species-labelled-label">${escapeHtml(inLabel)}</span>${vTail}</div>`
}

/*
Les 12 cases de la frise sans label interne — parité MonthStripCells :
fond surfaceVariant si inactif, couleur du groupe si actif, bordure primary
1.5px sur le mois courant.
*/
function renderMonthStrip(inBits: number, inActiveClass: string, inMonth0: number): string
{
	const vCells: string[] = []
	for (let vMonth0 = 0; vMonth0 < 12; vMonth0++)
	{
		const vClasses = ['species-month-cell']
		if (isMonthInBitfield(inBits, vMonth0)) vClasses.push(inActiveClass)
		if (vMonth0 === inMonth0) vClasses.push('species-month-cell-current')
		vCells.push(`<span class="${vClasses.join(' ')}"></span>`)
	}
	return `<div class="species-month-strip" aria-hidden="true">${vCells.join('')}</div>`
}

/*
Anti-redite — parité stripLabelPrefix (ReconnaitreBlock.kt) : si le texte
commence par le label (insensible à la casse), retire le préfixe, nettoie
la ponctuation/espaces de tête (set Kotlin exact, U+00A0 inclus) et
re-capitalise. Si le reste est vide, texte original inchangé.
*/
function stripLabelPrefix(inText: string, inLabel: string): string
{
	if (!inText.toLowerCase().startsWith(inLabel.toLowerCase())) return inText
	// Set exact du trimStart Kotlin : ' ', ':', '-', '—', U+00A0, ',', '.'.
	const kTrimChars = ' :-— ,.'
	let vStart = inLabel.length
	while (vStart < inText.length && kTrimChars.includes(inText.charAt(vStart))) vStart++
	const vRest = inText.slice(vStart)
	if (vRest.length === 0) return inText
	return capitalizeFirstFr(vRest)
}

/*
Bloc « Forces & faiblesses » — parité ForcesFaiblessesBlock.kt : atouts puis
limites, chaque item introduit par un glyphe (« + » vert atout, « − » écorce
limite), sans sous-titres de section. Garde de tête : rien si les deux
listes sont vides.
*/
function renderForcesFaiblesses(inEss: SpeciesEss): string
{
	const vAtouts = inEss.atouts ?? []
	const vLimites = inEss.limites ?? []
	if (vAtouts.length === 0 && vLimites.length === 0) return ''

	const vRows = [
		...vAtouts.map((inText) => renderGlyphRow('+', 'species-ff-glyph-plus', inText)),
		// U+2212 (signe moins) — même glyphe que le "−" Kotlin.
		...vLimites.map((inText) => renderGlyphRow('−', 'species-ff-glyph-minus', inText)),
	].join('')

	return `
		<section class="species-card species-card-reconnaitre">
			<h3 class="species-section-title">Forces &amp; faiblesses</h3>
			<div class="species-ff-list">${vRows}</div>
		</section>
	`
}

/* Row glyphe + texte — parité GlyphRow (ForcesFaiblessesBlock.kt). */
function renderGlyphRow(inGlyph: string, inGlyphClass: string, inText: string): string
{
	return `
		<div class="species-ff-row">
			<span class="species-ff-glyph ${inGlyphClass}" aria-hidden="true">${escapeHtml(inGlyph)}</span>
			<span class="species-ff-text">${escapeHtml(inText)}</span>
		</div>
	`
}

/*
"Remarquables de cette espèce" card. Mirrors Android v1.1.0 — a section
that lists every remarquable whose ArbreFeature.speciesIndex matches the
current species. Captured remarquables clickable to their detail screen;
non-captured ones rendered as silhouette ??? rows (parity Android
LockedRemarquableRow — the user can see the gravity of what's left to
find without revealing the trees themselves).
*/
function renderRemarquablesSection(inRows: RemarquableForSpeciesRow[]): string
{
	if (inRows.length === 0) return ''  // species without any remarquable: don't render the card at all

	const vTotal = inRows.length
	const vRowsHtml = inRows.map(renderRemarquableForSpeciesRow).join('')

	// Titre format Android « Arbres remarquables de cette espèce (8) » —
	// total seul, sans ratio (arbitrage parité stricte 2026-06-10).
	return `
		<section class="species-card">
			<h3 class="species-section-title">
				Arbres remarquables de cette espèce (${vTotal})
			</h3>
			<div class="species-rmq-list">${vRowsHtml}</div>
		</section>
	`
}

function renderRemarquableForSpeciesRow(inRow: RemarquableForSpeciesRow): string
{
	// Parité Android : row découverte = adresse complète (sidecar v3),
	// row verrouillée = « ??? · 13e » (arrondissement seul, pas de leak).
	const vAttrs = attrsFor(inRow.arbreId)
	const vArrLabel = vAttrs && vAttrs.arr.kind !== 'other' ? arrKeyLabel(vAttrs.arr) : null
	const vAdresse = vAttrs?.adresse ?? null
	if (!inRow.captured)
	{
		// Locked row — silhouette + arrondissement révélé (parité Android
		// LockedRemarquableRow). Click also gated by the verrou inside
		// RemarquableDetailScreen so even a hand-built URL bounces.
		return `
			<div class="species-rmq-row species-rmq-row-locked" aria-disabled="true">
				<span class="species-rmq-row-glyph" aria-hidden="true">?</span>
				<span class="species-rmq-row-title">???${vArrLabel ? ` · ${escapeHtml(vArrLabel)}` : ''}</span>
			</div>
		`
	}
	const vRowText = vAdresse
		? escapeHtml(vAdresse)
		: `${vArrLabel ? `${escapeHtml(vArrLabel)} · ` : ''}<span class="species-rmq-row-id">id ${inRow.arbreId}</span>`
	return `
		<button class="species-rmq-row" data-arbre-id="${inRow.arbreId}" aria-label="Voir la fiche remarquable">
			<span class="species-rmq-row-glyph" aria-hidden="true">⭐</span>
			<span class="species-rmq-row-title">${vRowText}</span>
			<span class="species-rmq-row-chevron" aria-hidden="true">›</span>
		</button>
	`
}

/*
Card « À Paris » — parité AParisBlock.kt : fusionne l'ancien StatsBlock
(barres supprimées) et l'encart « L'essence à Paris ». Prose éditoriale en
intro, puis compteur (garanti dès que stats existe), mesures médianes,
arrondissements dominants / sur-représentés, lien PDF de la fiche essence
officielle et attribution source. Chaque section omise si donnée absente.
*/
function renderAParis(inInfo: SpeciesEntry): string
{
	const vParis = inInfo.ess?.paris && inInfo.ess.paris.length > 0 ? inInfo.ess.paris : null
	const vPdf = inInfo.pdf && inInfo.pdf.length > 0 ? inInfo.pdf : null
	const vParts: string[] = []

	if (vParis) vParts.push(`<p class="species-aparis-prose">${escapeHtml(vParis)}</p>`)

	// Compteur bodyLarge — « 294 arbres · 7,3 % des arbres parisiens ».
	vParts.push(`
		<div class="species-aparis-count">
			${kFrenchNumber.format(inInfo.stats.count)} arbres · ${kPercentFr1.format(inInfo.stats.proportion)} des arbres parisiens
		</div>
	`)

	// Mesures médianes joints par « · » — null-check seul, parité Kotlin
	// `medianHeightM?.let` (pas de garde > 0 : le build n'émet pas de 0).
	const vMeasures: string[] = []
	if (typeof inInfo.stats.medianHm === 'number') vMeasures.push(`Hauteur médiane ${inInfo.stats.medianHm} m`)
	if (typeof inInfo.stats.medianCircCm === 'number') vMeasures.push(`Circonférence médiane ${inInfo.stats.medianCircCm} cm`)
	if (vMeasures.length > 0) vParts.push(`<div class="species-aparis-measures">${escapeHtml(vMeasures.join(' · '))}</div>`)

	// Listes complètes (parité Kotlin, pas de slice — le JSON a ≤ 3 entrées).
	if (inInfo.stats.topArrAbs.length > 0)
	{
		const vText = inInfo.stats.topArrAbs.map((inA) => `${inA.arr} (${kFrenchNumber.format(inA.count)})`).join(' · ')
		vParts.push(renderArrLine('Plus nombreux : ', vText))
	}
	if (inInfo.stats.topArrOver.length > 0)
	{
		// Ratio absent → arrondissement seul (parité Kotlin ratio nullable,
		// même si le JSON courant n'a pas de ratio null).
		const vText = inInfo.stats.topArrOver.map((inA) =>
			typeof inA.ratio === 'number' ? `${inA.arr} (×${kRatioFr1.format(inA.ratio)})` : inA.arr,
		).join(' · ')
		vParts.push(renderArrLine('Sur-représentés : ', vText))
	}

	// Lien PDF inline — 📄/↗ équivalents texte des icônes PictureAsPdf /
	// OpenInNew (même approche que l'ancien bloc PDF standalone absorbé ici).
	if (vPdf)
	{
		vParts.push(`
			<a class="species-link species-aparis-pdf" href="${escapeAttr(vPdf)}" target="_blank" rel="noopener">
				<span aria-hidden="true">📄</span> Fiche essence (PDF) <span aria-hidden="true">↗</span>
			</a>
		`)
	}

	if (vParis || vPdf)
	{
		vParts.push('<div class="species-wiki-attribution">Source : Ville de Paris · Guide des essences</div>')
	}

	return `
		<section class="species-card species-card-reconnaitre">
			<h3 class="species-section-title">À Paris</h3>
			${vParts.join('')}
		</section>
	`
}

/* Ligne annotée « label (semibold) : liste » — parité ArrLine (AParisBlock.kt). */
function renderArrLine(inLabel: string, inText: string): string
{
	return `<div class="species-aparis-arrline"><span class="species-labelled-label">${escapeHtml(inLabel)}</span>${escapeHtml(inText)}</div>`
}

/*
Card « Son rôle en ville » — parité RoleEnVilleBlock.kt : proses de services
écosystémiques (climat, eau, biodiversité), repliée par défaut pour
raccourcir la page. Bascule sèche sans animation (contrat device de Max) —
wiring du header dans hydrate(), état porté par l'attribut `hidden`.
*/
function renderRoleEnVille(inSvc: SpeciesSvc): string
{
	const vItems: string[] = []
	if (inSvc.climat) vItems.push(renderServiceItem('Climat', inSvc.climat))
	if (inSvc.eau) vItems.push(renderServiceItem('Eau', inSvc.eau))
	if (inSvc.biodiv) vItems.push(renderServiceItem('Biodiversité', inSvc.biodiv))
	if (vItems.length === 0) return ''  // groupe svc présent mais vide : rien (call-site Android gate sur non-null)

	return `
		<section class="species-card species-card-reconnaitre">
			<button class="species-role-header" id="species-role-header" aria-expanded="false">
				<span class="species-section-title">Son rôle en ville</span>
				<span class="species-role-chevron" id="species-role-chevron" aria-hidden="true">▾</span>
			</button>
			<div class="species-role-items" id="species-role-items" hidden>
				${vItems.join('')}
			</div>
		</section>
	`
}

/* Item label + prose — parité ServiceItem (RoleEnVilleBlock.kt). */
function renderServiceItem(inLabel: string, inProse: string): string
{
	return `
		<div class="species-role-item">
			<div class="species-role-item-label">${escapeHtml(inLabel)}</div>
			<div class="species-role-item-prose">${escapeHtml(inProse)}</div>
		</div>
	`
}

/* Blank au sens Kotlin isNullOrBlank : absent, vide ou espaces seuls. */
function isBlank(inText: string | undefined | null): boolean
{
	return !inText || inText.trim().length === 0
}

/*
Card « À propos » — parité WikipediaBlock.kt + logique d'appel de
SpeciesDetailScreen.kt : summary espèce si présent, sinon repli sur le
résumé du genre (titre « À propos du genre X » explicite), sinon message
vide sans lien ni attribution. Toujours rendue — l'item Android est
inconditionnel.
*/
function renderWikiOrFallback(inModel: ViewModel): string
{
	const vSummary = inModel.speciesInfo?.summary
	if (vSummary && !isBlank(vSummary))
	{
		const vSlug = inModel.speciesInfo?.wp
		return renderWikiCard('À propos', vSummary, vSlug && !isBlank(vSlug) ? vSlug : null)
	}
	const vFallback = inModel.genreFallback
	if (vFallback && vFallback.summary && !isBlank(vFallback.summary))
	{
		// Le lien et l'attribution pointent alors l'article du GENRE.
		return renderWikiCard(
			`À propos du genre ${vFallback.fr ?? vFallback.g}`,
			vFallback.summary,
			vFallback.wp && !isBlank(vFallback.wp) ? vFallback.wp : null,
		)
	}
	// Branche emptyMessage — parité : ni « Lire sur Wikipedia » ni source.
	return `
		<section class="species-card species-card-wiki">
			<h3 class="species-section-title">À propos</h3>
			<p class="species-wiki-empty">Pas d'info encyclopédique disponible pour cette espèce.</p>
		</section>
	`
}

/*
Corps commun des deux branches à summary : texte clampé à 5 lignes
(parité collapsedLines = 5), toggle « Lire la suite »/« Réduire » masqué
tant que hydrate() n'a pas mesuré d'overflow réel, lien article, puis
attribution cliquable vers la licence CC BY-SA (parité WikipediaBlock :
la ligne source est un lien).
*/
function renderWikiCard(inTitle: string, inSummary: string, inSlug: string | null): string
{
	const vLink = inSlug ? `
		<a class="species-link" href="https://fr.wikipedia.org/wiki/${encodeURIComponent(inSlug)}" target="_blank" rel="noopener">
			Lire sur Wikipedia →
		</a>
	` : ''
	return `
		<section class="species-card species-card-wiki">
			<h3 class="species-section-title">${escapeHtml(inTitle)}</h3>
			<p class="species-wiki-text species-wiki-text-clamped" id="species-wiki-text">${escapeHtml(inSummary)}</p>
			<button class="species-wiki-toggle" id="species-wiki-toggle" hidden>Lire la suite</button>
			${vLink}
			<a class="species-wiki-attribution" href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noopener">
				Source : Wikipédia FR · CC BY-SA 4.0 ↗
			</a>
		</section>
	`
}

function renderGalleryShell(_inSk: number, inCaptureCount: number): string
{
	// « Tes photos (N) » — parité Android PhotoGallery title.
	if (inCaptureCount === 0)
	{
		return `
			<section class="species-card species-empty-gallery">
				<h3 class="species-section-title">Tes photos</h3>
				<p class="species-section-blurb">
					Pas encore de capture pour cette espèce. Reviens après l'avoir
					rencontrée sur la carte.
				</p>
			</section>
		`
	}
	return `
		<section class="species-card">
			<h3 class="species-section-title" id="species-gallery-title">Tes photos (${inCaptureCount})</h3>
			<div class="species-gallery" id="species-gallery"></div>
		</section>
	`
}

const kLongPressMs: number = 500  // matches PhotoGallery.ts; threshold for thumbnail long-press → DeleteCaptureDialog

/*
Async-load photo Blobs for the user's captures of this species and
inject <img> tags. Object URLs are tracked in module scope so we can
revoke them on next mount, avoiding the IDB Blob memory leak that creeps
up in long-lived single-page apps.

Each thumb wires:
- click           → openLightbox at this index (with jump-to-map + delete
                    callbacks if the arbre's position is known).
- long-press 500ms → DeleteCaptureDialog directly, skipping the lightbox.

If `inHighlightId` matches one of the rendered captures (handed off
from the profile timeline through the route's `highlight` query
param), that thumbnail is wrapped with `data-highlighted="true"` so
the CSS pulse can fire, and the gallery scrolls it into view.
*/
async function hydrateGallery(
	inRoot: HTMLElement,
	inSk: number,
	inCaptures: Capture[],
	inHighlightId: number | null,
	inDisplayName: string,
): Promise<void>
{
	const vGallery = inRoot.querySelector<HTMLElement>('#species-gallery')
	if (!vGallery) return

	// Resolve lat/lon per arbreId so the lightbox can offer "Voir sur
	// la carte" → fly-to. The features array is shared across screens
	// and already memoised, so the call is cheap after the first map
	// mount.
	let vFeaturesById = new Map<number, ArbreFeature>()
	try
	{
		const vAll = await ensureArbresFeatures()
		const vWanted = new Set(inCaptures.map((inC) => inC.arbreId))  // small N, useful early-out
		for (const vF of vAll) if (vWanted.has(vF.id)) vFeaturesById.set(vF.id, vF)
	}
	catch (vErr) { console.warn('arbresFeatures lookup failed', vErr) }

	const vPhotos: LightboxPhoto[] = []  // accumulated for the lightbox handler — mirrors the thumbnail order
	let vHighlightedImg: HTMLImageElement | null = null  // resolved at most once, scrolled into view after the loop
	for (const vCap of inCaptures)
	{
		if (!vCap.photoFilename) continue
		if (typeof vCap.id !== 'number') continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)  // ephemeral, revoked on next mount
		fActiveObjectUrls.push(vUrl)
		const vCaption = `Capture du ${kFrenchDate.format(new Date(vCap.timestamp))}`
		const vFeature = vFeaturesById.get(vCap.arbreId)  // null if the dataset doesn't carry this id (rare)
		const vImg = document.createElement('img')
		vImg.className = 'species-thumb'
		vImg.alt = vCaption
		vImg.src = vUrl
		vImg.loading = 'lazy'
		vImg.dataset.captureId = String(vCap.id)
		if (inHighlightId !== null && vCap.id === inHighlightId)
		{
			vImg.dataset.highlighted = 'true'  // CSS pulse keys off this attribute
			vHighlightedImg = vImg
		}
		const vIndexInGallery = vPhotos.length  // freeze the lightbox index for this thumb's click handler
		vPhotos.push({
			url: vUrl,
			caption: vCaption,
			captureId: vCap.id,
			arbreId: vCap.arbreId,
			lat: vFeature?.lat,
			lon: vFeature?.lon,
		})

		// click vs long-press detector. The long-press timer fires after
		// kLongPressMs; we set `vConsumed` so the synthesised click that
		// follows release doesn't also open the lightbox.
		let vTimer: number | null = null
		let vConsumed: boolean = false
		const vStart = (): void => {
			vConsumed = false
			if (vTimer !== null) window.clearTimeout(vTimer)
			vTimer = window.setTimeout(() => {
				vTimer = null
				vConsumed = true
				void handleSpeciesDelete(inRoot, inSk, vCap.id as number, inDisplayName, vPhotos.length === 1)
			}, kLongPressMs)
		}
		const vCancel = (): void => {
			if (vTimer !== null) { window.clearTimeout(vTimer); vTimer = null }
		}
		vImg.addEventListener('touchstart', vStart, { passive: true })
		vImg.addEventListener('touchend', vCancel)
		vImg.addEventListener('touchmove', vCancel)
		vImg.addEventListener('touchcancel', vCancel)
		vImg.addEventListener('mousedown', vStart)
		vImg.addEventListener('mouseup', vCancel)
		vImg.addEventListener('mouseleave', vCancel)
		vImg.addEventListener('click', () => {
			if (vConsumed) return
			openSpeciesLightbox(inRoot, inSk, vPhotos, vIndexInGallery, inDisplayName)
		})
		vGallery.appendChild(vImg)
	}

	// After the gallery is fully painted, scroll the highlighted
	// thumbnail into view. Wait one rAF so the layout has settled.
	if (vHighlightedImg)
	{
		const vTarget = vHighlightedImg
		requestAnimationFrame(() => {
			try { vTarget.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }) }
			catch { vTarget.scrollIntoView() }  // older browsers fall back to no-options form
		})
	}
}

/*
Open the lightbox starting at a given index. Wires the jump-to-map
button (closes the lightbox and navigates `#/map?pulseArbreId=...`)
and the trash button (delete after confirmation). The lightbox owns
the in-memory splice on confirmed delete; we update the DOM gallery
afterwards by removing the matching thumb.
*/
function openSpeciesLightbox(
	inRoot: HTMLElement,
	inSk: number,
	inPhotos: LightboxPhoto[],
	inStartIndex: number,
	inDisplayName: string,
): void
{
	const vOptions: LightboxOptions = {
		onJumpToMap: (inIdx) => {
			const vP = inPhotos[inIdx]
			if (!vP || typeof vP.arbreId !== 'number') return
			navigate(kPaths.map(vP.arbreId))
		},
		onDelete: async (inIdx) => {
			const vP = inPhotos[inIdx]
			if (!vP || typeof vP.captureId !== 'number') return false
			const vIsLast = inPhotos.length === 1
			return handleSpeciesDelete(inRoot, inSk, vP.captureId, inDisplayName, vIsLast)
		},
	}
	openLightbox(inPhotos, inStartIndex, vOptions)
}

/*
Confirm with the user, run the IDB delete, repaint the gallery in
place. Returns true when the delete landed so the lightbox can
splice + advance. If this was the species' last capture, navigates to
the map (parity Android v1.0.2: re-locked entries kick the user out
of the now-dead fiche).
*/
async function handleSpeciesDelete(
	inRoot: HTMLElement,
	_inSk: number,
	inCaptureId: number,
	inDisplayName: string,
	inIsLast: boolean,
): Promise<boolean>
{
	const vConfirmed = await confirmDeleteCapture({ kind: 'species', displayName: inDisplayName, isLast: inIsLast })
	if (!vConfirmed) return false
	try
	{
		const vRes = await deleteCapture(inCaptureId)
		// Remove the thumb from the gallery + update the counter, or
		// punt to the map when there's nothing left to look at.
		const vGallery = inRoot.querySelector<HTMLElement>('#species-gallery')
		const vThumb = vGallery?.querySelector<HTMLElement>(`img[data-capture-id="${inCaptureId}"]`)
		vThumb?.remove()
		const vTitle = inRoot.querySelector<HTMLElement>('#species-gallery-title')
		if (vTitle)
		{
			const vLeft = vGallery?.querySelectorAll('.species-thumb').length ?? 0
			vTitle.textContent = `Tes photos (${vLeft})`
		}
		if (vRes.wasLastForSpecies)
		{
			// The species fiche has nothing left to reveal — go back to
			// the map so the user isn't staring at a locked entry.
			navigate(kPaths.map())
		}
		return true
	}
	catch (vErr)
	{
		console.warn('deleteCapture failed', vErr)
		return false
	}
}

function revokeObjectUrls(): void
{
	for (const vUrl of fActiveObjectUrls) URL.revokeObjectURL(vUrl)
	fActiveObjectUrls = []
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function escapeAttr(inText: string): string { return escapeHtml(inText) }
